/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@compiled/react';
import {
	CardAction,
	ElementName,
	SmartLinkSize,
	TitleBlock,
	SmartLinkPosition,
	SmartLinkTheme,
	type CardProps,
} from '@atlaskit/smart-card';
import { CardSSR as Card, type CardSSRProps } from '@atlaskit/smart-card/ssr';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { RemoveButton } from './remove-button/index.tsx';

const uiOptions: CardProps['ui'] = {
	clickableContainer: true,
	hideBackground: true,
	hideElevation: true,
	hidePadding: true,
	size: SmartLinkSize.Medium,
};

const uiOptionsWithPlaceholder: CardProps['ui'] = {
	...uiOptions,
	removeBlockRestriction: true,
};

const actionOptions = {
	hide: false,
	exclude: [CardAction.ChangeStatusAction],
};

type TitleBlockProps = Parameters<typeof TitleBlock>[0];
export type Props = Pick<
	CardSSRProps,
	'url' | 'showHoverPreview' | 'onError' | 'placeholderData' | 'hoverPreviewOptions' | 'onResolve'
> &
	Pick<
		TitleBlockProps,
		'anchorTarget' | 'text' | 'anchorRef' | 'icon' | 'metadata' | 'actions' | 'hideRetry'
	> & {
		/** When defined this will display a remove button after the link that calls `onRemove` when clicked. */
		onRemove?: () => void;
		/** When defined, this will display a simplified UI for smart links with trimmed paddding. */
		isSimplifiedUI?: boolean;
	};

export const SimpleInlineSmartLink = ({
	onRemove,
	url,
	anchorTarget,
	showHoverPreview = true,
	text,
	anchorRef,
	onError,
	placeholderData,
	metadata,
	actions,
	hideRetry,
	hoverPreviewOptions,
	isSimplifiedUI = false,
	icon,
	onResolve,
}: Props): JSX.Element => {
	return (
		<Box
			paddingBlock="space.025"
			paddingInline="space.050"
			xcss={isSimplifiedUI ? simplifiedInlineStyles : inlineStyles}
		>
			<Card
				appearance="inline"
				url={url}
				ui={{
					...(fg('jfp-magma-fix-url-field-ssr-hydration') ||
					(placeholderData && fg('goals_ssr_smart_card'))
						? uiOptionsWithPlaceholder
						: uiOptions),
					theme: isSimplifiedUI ? SmartLinkTheme.Black : undefined,
				}}
				actionOptions={actionOptions}
				showHoverPreview={showHoverPreview}
				placeholderData={placeholderData}
				onError={onError}
				hoverPreviewOptions={hoverPreviewOptions}
				onResolve={onResolve}
			>
				<TitleBlock
					hideTitleTooltip
					maxLines={1}
					metadata={
						metadata || [
							{
								name: ElementName.State,
							},
						]
					}
					anchorTarget={anchorTarget ?? '_self'}
					text={text}
					css={[isSimplifiedUI ? simplifiedTitleBlockStyles : titleBlockStyles]}
					anchorRef={anchorRef}
					{...(fg('goals_ssr_smart_card') ? { hideIconLoadingSkeleton: !!placeholderData } : {})}
					{...(isSimplifiedUI ? { position: SmartLinkPosition.Center } : {})}
					actions={actions}
					icon={icon}
					hideRetry={hideRetry}
				/>
			</Card>
			{onRemove && <RemoveButton onClick={onRemove} />}
		</Box>
	);
};

const inlineStyles = xcss({
	backgroundColor: 'elevation.surface.raised',
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderRadius: 'radius.small',
	display: 'inline-flex',
	gap: 'space.050',
});

const titleBlockStyles = css({
	gap: token('space.050'),
});

const simplifiedInlineStyles = xcss({
	marginBlock: 'space.025',
	backgroundColor: 'elevation.surface.raised',
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderWidth: 'border.width',
	borderRadius: 'radius.small',
	display: 'inline-flex',
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
	},
	paddingInlineStart: 'space.025',
	paddingInlineEnd: 'space.0',
	paddingBlock: 'space.0',
});

const simplifiedTitleBlockStyles = css({
	gap: token('space.050'),
	height: '20px',
	maxHeight: '20px',
	minHeight: '20px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-smart-element-link]': {
		wordBreak: 'break-all',
		paddingInlineEnd: token('space.025'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& .actions-button-group': {
		backgroundColor: token('color.background.neutral'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-testid="smart-action"]': {
			'&:focus-visible': {
				outlineOffset: token('space.negative.050'),
			},
		},
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'[data-smart-element-lozenge]': {
		paddingBottom: token('space.025'),
		paddingRight: token('space.025'),
	},
});
